import { createContext, FC, ReactNode, useContext, useRef } from 'react';
import { createStore, useStore } from 'zustand';
import { handleError } from '../gql/config/URQLProvider';
import { Location, useGetMeQuery } from '../gql/graphql.generated';
import { useAdminLocationStore } from './useAdminLocationStore';

export interface LocationService {
  selectedLocation?: Location;
  locationList: Location[];
  setSelectedLocation: (newVal?: Location) => void;
  mappedLocations: CheckboxLocation[];
  setMappedLocations: (newVal: any) => void;
  selectedShiftLocations: BaseLocation[];
  setSelectedShiftLocations: (newVal: any) => void;
}

export interface CheckboxLocation extends BaseLocation {
  descriptionMarkup: ReactNode;
}

export interface BaseLocation {
  key: string;
  name: string;
  checked: boolean;
}

type LocationStore = ReturnType<typeof makeStore>;

const STORAGE_KEY_PREFIX = 'selectedLocation';
const SELECTED_SHIFT_LOCATIONS_KEY_PREFIX = 'selectedShiftLocations';

const readStoredJson = <T,>(storageKey: string): T | undefined => {
  const storedValue = localStorage.getItem(storageKey);
  if (!storedValue || storedValue === 'undefined') {
    localStorage.removeItem(storageKey);
    return undefined;
  }

  try {
    return JSON.parse(storedValue) as T;
  } catch {
    localStorage.removeItem(storageKey);
    return undefined;
  }
};

const makeStore = (
  storageKey: string,
  selectedShiftLocationStorageKey: string,
  defaultLocation: Location | undefined,
  defaultShiftLocations: BaseLocation[],
  locationList: Location[]
) =>
  createStore<LocationService>()(set => ({
    selectedLocation: defaultLocation,
    locationList,
    setSelectedLocation: (newVal?: Location) => {
      if (newVal) {
        localStorage.setItem(storageKey, JSON.stringify(newVal));
      } else {
        localStorage.removeItem(storageKey);
      }
      set({ selectedLocation: newVal });
    },
    mappedLocations: [],
    setMappedLocations: (newVal: any) => {
      localStorage.setItem(storageKey, JSON.stringify(newVal));
      set({ mappedLocations: newVal });
    },
    selectedShiftLocations: defaultShiftLocations,
    setSelectedShiftLocations: (newVal: any) => {
      localStorage.setItem(selectedShiftLocationStorageKey, JSON.stringify(newVal));
      set({ selectedShiftLocations: newVal });
    }
  }));

const LocationContext = createContext<LocationStore | null>(null);

export const LocationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [{ data: meData, error: meError }] = useGetMeQuery();
  if (meError) handleError(meError);

  const storageKey = `${STORAGE_KEY_PREFIX}-${meData?.myEmployee?.identityId}`;
  const selectedShiftLocationStorageKey = `${SELECTED_SHIFT_LOCATIONS_KEY_PREFIX}-${meData?.myEmployee?.identityId}`;
  const locationList = (meData?.myEmployee?.locations as Location[]) || [];

  const actingLocation = useAdminLocationStore(s => s.actingLocation);

  const getDefaultLocation = (): Location | undefined => {
    if (actingLocation) {
      return actingLocation;
    }

    const objectInStorage = readStoredJson<Location>(storageKey);
    if (objectInStorage) {
      if (locationList.find(loc => loc.id === objectInStorage.id)) {
        return objectInStorage;
      }
    }

    if (meData?.myEmployee?.locations && meData.myEmployee.locations.length > 0) {
      const defaultLocation = meData.myEmployee.locations[0] as Location;
      localStorage.setItem(storageKey, JSON.stringify(defaultLocation));
      return defaultLocation;
    }

    return undefined;
  };

  const getDefaultShiftLocations = (): BaseLocation[] => {
    const parsed = readStoredJson<BaseLocation[]>(selectedShiftLocationStorageKey);
    if (parsed) {
      return parsed;
    }

    const locations: BaseLocation[] = locationList.map(location => ({
      key: String(location.id),
      name: location.name,
      checked: true
    }));
    localStorage.setItem(selectedShiftLocationStorageKey, JSON.stringify(locations));
    return locations;
  };

  const storeRef = useRef<LocationStore | null>(null);
  if (!storeRef.current) {
    storeRef.current = makeStore(
      storageKey,
      selectedShiftLocationStorageKey,
      getDefaultLocation(),
      getDefaultShiftLocations(),
      locationList
    );
  }

  return <LocationContext.Provider value={storeRef.current}>{children}</LocationContext.Provider>;
};

export const useLocationService = <T,>(selector: (state: LocationService) => T): T => {
  const store = useContext(LocationContext);
  if (!store) {
    throw new Error('useLocationService must be used within a LocationProvider');
  }
  return useStore(store, selector);
};

export const useOptionalLocationService = <T,>(
  selector: (state: LocationService) => T
): T | undefined => {
  const store = useContext(LocationContext);
  return store ? useStore(store, selector) : undefined;
};
